/* How a day read from the book is read, the same way everywhere on the server.
 *
 * `recheckMove` looks at a day right before a save, and the Optimizer's planner looks
 * at the same day to decide what to propose. If they read a booking differently, the
 * planner proposes moves the re-check then refuses, or the re-check waves through what
 * the planner would never have offered. So what they share lives here: the shape is
 * the one `buildDay` in the function's recheck.js produces.
 *
 * Time is one convention throughout: milliseconds of the salon's wall clock, labelled
 * UTC, the same as the mirror.
 *
 * Pure: it asks nothing but the arguments it is given. Intl is the only outside thing,
 * and it is a lookup table.
 */
import { optionTurnType } from './option-type.js';

export function overlaps(a, b) { return a.from < b.to && a.to > b.from; }

export function wallMs(naive) {
  if (!naive) return null;
  var s = String(naive).replace(' ', 'T').replace(/([zZ]|[+-]\d\d:?\d\d)$/, '');
  if (s.length === 16) s += ':00';
  var ms = Date.parse(s.slice(0, 19) + 'Z');
  return Number.isNaN(ms) ? null : ms;
}

// A real instant, as what the clock on the salon's wall read at that moment.
export function instantToWall(instant, timeZone) {
  var parts = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(instant).forEach(function (p) { parts[p.type] = p.value; });
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second));
}

export function partSpan(appt, part) {
  var from = wallMs(part.starts_at || appt.starts_at);
  var to = wallMs(part.ends_at);
  if (to == null && from != null && part.duration_minutes) to = from + part.duration_minutes * 60000;
  return { from: from, to: to };
}

export function requested(part) {
  // Null reads as requested: only Mangomint saying Anyone frees a booking.
  if (part.staff_requested_type !== 'Anyone') return true;
  // The same for a second technician: named with no type read is not Anyone.
  if (part.additional_staff_id != null) return part.additional_staff_requested_type !== 'Anyone';
  return part.additional_staff_requested_type != null && part.additional_staff_requested_type !== 'Anyone';
}

export function allowedStaffFor(part, day) {
  var allowed = null;
  (part.service_option_ids || []).forEach(function (id) {
    var rule = day.optionRules[id];
    if (!rule || !rule.staffIds.length) return;
    allowed = allowed ? allowed.filter(function (s) { return rule.staffIds.indexOf(s) !== -1; }) : rule.staffIds.slice();
  });
  return allowed;
}

export function mappedServiceFor(part, day) {
  var base = day.serviceMap[part.service_id] || null;
  var options = (part.service_option_ids || []).map(function (id) { return { id: id }; });
  return optionTurnType(options, day.optionRules, base, function (id) { return !!day.services[id]; }) || base;
}

/* Their shift spans, with a typed Out time standing in for the end of the day. It
 * beats Mangomint's whichever way it moves things. A typed In time is not read:
 * the board keeps it to place someone in the rotation, and it cannot make someone
 * Mangomint has not got working yet available any earlier. With no shift from
 * Mangomint at all, the typed time is all there is, and it has no start to go by. */
export function spansFor(day, staffId) {
  var spans = day.shifts[staffId] || [];
  var typed = day.board && day.board[staffId] ? day.board[staffId].outAt : null;
  if (typed == null) return spans;
  if (!spans.length) return [{ from: -Infinity, to: typed }];
  var kept = spans.filter(function (s) { return s.from < typed; });
  if (!kept.length) return [];
  return kept.map(function (s, i) { return i === kept.length - 1 ? { from: s.from, to: typed } : s; });
}
